import { useState, type ReactNode } from 'react';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { cn } from '../../utils/cn';
import UpgradeAccountModal from './UpgradeAccountModal';

export interface AccountLockNoticeProps {
    children: ReactNode;
    /** Offer to create an account — the lock lifts once the guest registers. */
    showCreateAccount?: boolean;
    /** Stacking level for the account modal, when the notice sits inside another overlay. */
    modalZIndexClassName?: string;
    className?: string;
    'data-testid'?: string;
}

/** Explains why something is off-limits for this account (issue #332), with a way to lift it when there is one. */
export default function AccountLockNotice({
    children,
    showCreateAccount = false,
    modalZIndexClassName,
    className,
    'data-testid': testId = 'account-lock-notice',
}: AccountLockNoticeProps) {
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    return (
        <div
            role="note"
            data-testid={testId}
            className={cn(
                'flex flex-wrap items-center gap-3 rounded-tile border border-status-warning/40 bg-status-warning/15 px-4 py-3',
                className,
            )}
        >
            <Icon name="lock" size={18} className="shrink-0 text-status-warning" />
            <p className="min-w-0 flex-1 text-body text-content-primary">{children}</p>
            {showCreateAccount && (
                <>
                    <Button type="button" size="sm" onClick={() => setUpgradeOpen(true)}>
                        Create account
                    </Button>
                    <UpgradeAccountModal
                        open={upgradeOpen}
                        onClose={() => setUpgradeOpen(false)}
                        zIndexClassName={modalZIndexClassName}
                    />
                </>
            )}
        </div>
    );
}
